import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { authService } from '../services/auth.service';
import './Securite.css';

interface Connexion {
  id: number;
  userId: number;
  userEmail: string;
  userNom: string;
  ipAddress: string;
  userAgent: string;
  statut: string;
  motifEchec: string;
  createdAt: string;
}

interface StatsConnexion {
  total: number;
  aujourd_hui: number;
  echecs: number;
  derniere: Connexion | null;
}

export default function Securite() {
  const user = authService.getUser();
  const [ancienMdp, setAncienMdp] = useState('');
  const [nouveauMdp, setNouveauMdp] = useState('');
  const [confirmMdp, setConfirmMdp] = useState('');
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');
  const [chargement, setChargement] = useState(false);
  const [connexions, setConnexions] = useState<Connexion[]>([]);
  const [statsConnexion, setStatsConnexion] = useState<StatsConnexion | null>(null);
  const [onglet, setOnglet] = useState<'mdp' | 'historique'>('mdp');
  const [filtreStatut, setFiltreStatut] = useState<'tous' | 'succes' | 'echec'>('tous');

  useEffect(() => {
    chargerConnexions();
  }, []);

  const chargerConnexions = async () => {
    try {
      const [connexionsRes, statsRes] = await Promise.all([
        api.get('/auth/connexions'),
        api.get('/auth/connexions/stats'),
      ]);
      setConnexions(connexionsRes.data);
      setStatsConnexion(statsRes.data);
    } catch (e) {}
  };

  const changerMotDePasse = async () => {
    setErreur('');
    if (!ancienMdp || !nouveauMdp || !confirmMdp) {
      setErreur('Tous les champs sont obligatoires');
      return;
    }
    if (nouveauMdp !== confirmMdp) {
      setErreur('Les deux nouveaux mots de passe ne correspondent pas');
      return;
    }
    if (nouveauMdp.length < 8) {
      setErreur('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    setChargement(true);
    try {
      await api.post('/auth/login', {
        email: user?.email,
        password: ancienMdp,
      });
      await api.put(`/users/${user?.id}`, { password: nouveauMdp });
      setSucces('Mot de passe modifié avec succès. Reconnectez-vous.');
      setAncienMdp('');
      setNouveauMdp('');
      setConfirmMdp('');
      setTimeout(() => {
        authService.logout();
        window.location.href = '/';
      }, 2000);
    } catch (e: any) {
      setErreur('Ancien mot de passe incorrect');
    } finally {
      setChargement(false);
    }
  };

  const forcerDeconnexion = () => {
    authService.logout();
    window.location.href = '/';
  };

  const connexionsFiltrees = connexions.filter(c =>
    filtreStatut === 'tous' || c.statut === filtreStatut
  );

  const parseUserAgent = (ua: string) => {
    if (!ua) return 'Navigateur inconnu';
    if (ua.includes('Chrome')) return '🌐 Chrome';
    if (ua.includes('Firefox')) return '🦊 Firefox';
    if (ua.includes('Safari')) return '🧭 Safari';
    if (ua.includes('Edge')) return '🔵 Edge';
    return '🌐 Navigateur';
  };

  const conseils = [
    { icone: '🔤', titre: 'Longueur minimale', desc: 'Utilisez au moins 12 caractères' },
    { icone: '🔢', titre: 'Mélangez les caractères', desc: 'Combinez majuscules, minuscules, chiffres et symboles' },
    { icone: '🚫', titre: 'Évitez le prévisible', desc: "N'utilisez pas votre nom, date de naissance ou \"123456\"" },
    { icone: '🔄', titre: 'Changez régulièrement', desc: 'Changez votre mot de passe tous les 3 mois' },
    { icone: '🔒', titre: 'Unique par service', desc: "N'utilisez pas le même mot de passe sur plusieurs applications" },
  ];

  return (
    <div className="securite-page">
      <div className="page-header">
        <div>
          <h1>Sécurité</h1>
          <p>Gérez la sécurité de votre compte NexaCom</p>
        </div>
      </div>

      {succes && <div className="alert-succes">✅ {succes}</div>}
      {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

      {/* ONGLETS */}
      <div className="sec-tabs">
        <button className={onglet === 'mdp' ? 'active' : ''}
          onClick={() => setOnglet('mdp')}>
          🔑 Mot de passe
        </button>
        <button className={onglet === 'historique' ? 'active' : ''}
          onClick={() => setOnglet('historique')}>
          📋 Historique connexions
          {statsConnexion && statsConnexion.echecs > 0 && (
            <span className="sec-badge">{statsConnexion.echecs}</span>
          )}
        </button>
      </div>

      {/* ONGLET MOT DE PASSE */}
      {onglet === 'mdp' && (
        <div className="securite-grid">
          <div className="sec-card">
            <div className="sec-card-titre">🔑 Changer le mot de passe</div>
            <div className="sec-card-desc">
              Compte connecté : <strong>{user?.email}</strong>
            </div>

            <div className="form-group">
              <label>Mot de passe actuel</label>
              <input type="password" value={ancienMdp}
                onChange={e => setAncienMdp(e.target.value)}
                placeholder="Votre mot de passe actuel" />
            </div>
            <div className="form-group">
              <label>Nouveau mot de passe</label>
              <input type="password" value={nouveauMdp}
                onChange={e => setNouveauMdp(e.target.value)}
                placeholder="Minimum 8 caractères" />
              {nouveauMdp && (
                <div className="mdp-force">
                  <div className="mdp-barre">
                    <div className="mdp-niveau" style={{
                      width: nouveauMdp.length < 8 ? '25%' :
                        nouveauMdp.length < 12 ? '50%' :
                        /[A-Z]/.test(nouveauMdp) && /[0-9]/.test(nouveauMdp) ? '100%' : '75%',
                      background: nouveauMdp.length < 8 ? '#A32D2D' :
                        nouveauMdp.length < 12 ? '#854F0B' :
                        /[A-Z]/.test(nouveauMdp) && /[0-9]/.test(nouveauMdp) ? '#3B6D11' : '#185FA5',
                    }} />
                  </div>
                  <span className="mdp-label">
                    {nouveauMdp.length < 8 ? 'Trop court' :
                      nouveauMdp.length < 12 ? 'Acceptable' :
                      /[A-Z]/.test(nouveauMdp) && /[0-9]/.test(nouveauMdp) ? 'Fort ✓' : 'Bien'}
                  </span>
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Confirmer le nouveau mot de passe</label>
              <input type="password" value={confirmMdp}
                onChange={e => setConfirmMdp(e.target.value)}
                placeholder="Répétez le nouveau mot de passe"
                style={{
                  borderColor: confirmMdp && nouveauMdp !== confirmMdp ? '#A32D2D' :
                    confirmMdp && nouveauMdp === confirmMdp ? '#3B6D11' : '#E2E8F0'
                }} />
              {confirmMdp && nouveauMdp === confirmMdp && (
                <div style={{ fontSize: 11, color: '#3B6D11', marginTop: 4 }}>
                  ✓ Les mots de passe correspondent
                </div>
              )}
            </div>
            <button className="btn-primary" onClick={changerMotDePasse} disabled={chargement}>
              {chargement ? 'Vérification...' : '🔑 Changer le mot de passe'}
            </button>
          </div>

          <div className="sec-card">
            <div className="sec-card-titre">🔐 Session active</div>
            <div className="session-info">
              <div className="session-row">
                <span>Utilisateur</span>
                <span>{user?.prenom} {user?.nom}</span>
              </div>
              <div className="session-row">
                <span>Email</span>
                <span>{user?.email}</span>
              </div>
              <div className="session-row">
                <span>Rôle</span>
                <span style={{ textTransform: 'capitalize' }}>{user?.role}</span>
              </div>
              <div className="session-row">
                <span>Statut</span>
                <span style={{ color: '#3B6D11' }}>✓ Connecté</span>
              </div>
              {statsConnexion && (
                <div className="session-row">
                  <span>Connexions aujourd'hui</span>
                  <span>{statsConnexion.aujourd_hui}</span>
                </div>
              )}
            </div>
            <button className="btn-deconnexion" onClick={forcerDeconnexion}>
              🚪 Se déconnecter
            </button>
          </div>

          <div className="sec-card conseils-card">
            <div className="sec-card-titre">💡 Conseils de sécurité</div>
            <div className="conseils-liste">
              {conseils.map((c, i) => (
                <div key={i} className="conseil-item">
                  <div className="conseil-icon">{c.icone}</div>
                  <div>
                    <div className="conseil-titre">{c.titre}</div>
                    <div className="conseil-desc">{c.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="sec-card politique-card">
            <div className="sec-card-titre">🛡️ Politique de sécurité NexaCom</div>
            <div className="politique-liste">
              <div className="pol-item"><span className="pol-icone">✅</span><span>Les mots de passe sont chiffrés avec bcrypt</span></div>
              <div className="pol-item"><span className="pol-icone">✅</span><span>Les sessions expirent après 7 jours d'inactivité</span></div>
              <div className="pol-item"><span className="pol-icone">✅</span><span>Toutes les communications sont sécurisées par JWT</span></div>
              <div className="pol-item"><span className="pol-icone">✅</span><span>Chaque rôle a des accès limités à son périmètre</span></div>
              <div className="pol-item"><span className="pol-icone">✅</span><span>Aucun mot de passe n'est stocké en clair</span></div>
              <div className="pol-item"><span className="pol-icone">✅</span><span>Toutes les connexions sont enregistrées et traçables</span></div>
            </div>
          </div>
        </div>
      )}

      {/* ONGLET HISTORIQUE */}
      {onglet === 'historique' && (
        <div>
          {/* STATS */}
          {statsConnexion && (
            <div className="connexion-stats">
              <div className="cs-card total">
                <div className="cs-val">{statsConnexion.total}</div>
                <div className="cs-lbl">Total connexions</div>
              </div>
              <div className="cs-card aujourd-hui">
                <div className="cs-val">{statsConnexion.aujourd_hui}</div>
                <div className="cs-lbl">Aujourd'hui</div>
              </div>
              <div className={`cs-card ${statsConnexion.echecs > 0 ? 'echecs' : 'ok'}`}>
                <div className="cs-val">{statsConnexion.echecs}</div>
                <div className="cs-lbl">Tentatives échouées</div>
              </div>
              {statsConnexion.derniere && (
                <div className="cs-card derniere">
                  <div className="cs-val" style={{ fontSize: 13 }}>
                    {new Date(statsConnexion.derniere.createdAt).toLocaleString('fr-FR')}
                  </div>
                  <div className="cs-lbl">Dernière connexion</div>
                </div>
              )}
            </div>
          )}

          {/* ALERTE ECHECS */}
          {statsConnexion && statsConnexion.echecs > 0 && (
            <div className="alert-erreur" style={{ marginBottom: 16 }}>
              🚨 {statsConnexion.echecs} tentative{statsConnexion.echecs > 1 ? 's' : ''} de
              connexion échouée{statsConnexion.echecs > 1 ? 's' : ''} détectée{statsConnexion.echecs > 1 ? 's' : ''}.
              Vérifiez si quelqu'un tente d'accéder à votre compte.
            </div>
          )}

          {/* FILTRES */}
          <div className="connexion-filtres">
            <div className="filtre-type">
              {[
                { val: 'tous', label: 'Toutes' },
                { val: 'succes', label: '✓ Réussies' },
                { val: 'echec', label: '✗ Échouées' },
              ].map(f => (
                <button key={f.val}
                  className={filtreStatut === f.val ? 'active' : ''}
                  onClick={() => setFiltreStatut(f.val as any)}>
                  {f.label}
                </button>
              ))}
            </div>
            <button className="btn-secondary" onClick={chargerConnexions}>
              🔄 Actualiser
            </button>
          </div>

          {/* TABLEAU */}
          <div className="connexion-table">
            <table>
              <thead>
                <tr>
                  <th>Date & Heure</th>
                  <th>Utilisateur</th>
                  <th>Adresse IP</th>
                  <th>Navigateur</th>
                  <th>Statut</th>
                  <th>Détails</th>
                </tr>
              </thead>
              <tbody>
                {connexionsFiltrees.map(c => (
                  <tr key={c.id} className={c.statut === 'echec' ? 'row-echec' : ''}>
                    <td className="conn-date">
                      <div>{new Date(c.createdAt).toLocaleDateString('fr-FR')}</div>
                      <div className="conn-heure">
                        {new Date(c.createdAt).toLocaleTimeString('fr-FR', {
                          hour: '2-digit', minute: '2-digit', second: '2-digit'
                        })}
                      </div>
                    </td>
                    <td>
                      <div className="conn-user">{c.userNom}</div>
                      <div className="conn-email">{c.userEmail}</div>
                    </td>
                    <td className="conn-ip">{c.ipAddress || '—'}</td>
                    <td>{parseUserAgent(c.userAgent)}</td>
                    <td>
                      <span className={`conn-statut ${c.statut}`}>
                        {c.statut === 'succes' ? '✓ Réussie' : '✗ Échouée'}
                      </span>
                    </td>
                    <td className="conn-motif">
                      {c.motifEchec || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {connexionsFiltrees.length === 0 && (
              <div className="table-empty">Aucune connexion enregistrée</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}